import { DashboardSectionCollapsible } from '@g17eco/molecules/dashboard';
import { TableColumn } from '@g17eco/types/universalTracker';
import { DecimalInputProps } from './DecimalInput';
import { isDefined } from '@utils/index';
import { useState } from 'react';
import { UnitOverrideProps } from './OverrideUnitForm';
import { GLOBAL_UPDATE_CODE } from '../utils';
import { InputOverrideDataType, InputOverrideType, UnitConfigType, UnitTypeMapValue } from '../types';
import { InputOverrideField } from './InputOverrideField';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { VariationProps } from './VariationForm';
import { MandatoryProps, MandatoryForm } from './MandatoryForm';
import { isSimpleNumericColumnType } from '@utils/universalTracker';

const hasSetUnitOverride = ({
  columnCode,
  unitConfig,
}: {
  columnCode: string;
  unitConfig: Record<string, Record<string, InputOverrideDataType>>;
}) => {
  const unitTypes = Object.keys(unitConfig[columnCode]);
  return unitTypes.some((unitType) => {
    const value = unitConfig[columnCode][unitType];
    return value.isEnforced;
  });
};

const getColumnName = (column: TableColumn, hasColumnOverrides: boolean) => {
  return (
    <SimpleTooltip text={column.name}>
      <div className='d-flex align-items-center justify-content-between'>
        <div
          className='text-ThemeDark text-md fw-semibold text-truncate'
          style={{ maxWidth: hasColumnOverrides ? '360px' : '400px' }}
        >
          {column.name}
        </div>
        {hasColumnOverrides ? <i className='fal fa-bell-exclamation text-ThemeWarningMedium' /> : null}
      </div>
    </SimpleTooltip>
  );
};

interface Props
  extends DecimalInputProps,
    VariationProps,
    Omit<MandatoryProps, 'columnCode'>,
    Omit<UnitOverrideProps, 'unitTypeMap' | 'type'> {
  overridableColumns: TableColumn[];
  unitTypeMap: Record<string, Map<string, UnitTypeMapValue>>;
  unitConfig: UnitConfigType;
}

export const InputOverrideGroup = (props: Props) => {
  const { overridableColumns, unitTypeMap, unitConfig, decimal, variation, mandatory } = props;
  const [expandedColumns, setExpandedColumns] = useState<string[]>(
    overridableColumns.length === 1 ? [overridableColumns[0].code] : [],
  );

  if (overridableColumns.length === 0) {
    return (
      <>
        {Object.values(InputOverrideType).map((type) => (
          <InputOverrideField key={type} type={type} {...props} unitTypeMap={unitTypeMap[GLOBAL_UPDATE_CODE]} />
        ))}
      </>
    );
  }

  const hasColumnOverrides = (column: TableColumn) => {
    const columnCode = column.code;
    if (isSimpleNumericColumnType(column)) {
      return (
        isDefined(decimal[columnCode]?.value) ||
        isDefined(variation[columnCode]?.value) ||
        hasSetUnitOverride({ columnCode, unitConfig }) ||
        mandatory[columnCode]?.isEnforced
      );
    }
    return mandatory[columnCode]?.isEnforced;
  };

  const toggleExpandedColumns = (columnCode: string) => {
    return setExpandedColumns(
      expandedColumns.includes(columnCode)
        ? expandedColumns.filter((code) => code !== columnCode)
        : [...expandedColumns, columnCode],
    );
  };

  return (
    <>
      {overridableColumns.map((col) => (
        <DashboardSectionCollapsible
          key={col.code}
          title={getColumnName(col, hasColumnOverrides(col))}
          className='border-0'
          classes={{ whiteBoxContainer: 'border-0', body: 'pe-1' }}
          mb={0}
          padding={0}
          paddingInternal={0}
          isCollapsed={!expandedColumns.includes(col.code)}
          toggleCollapse={() => toggleExpandedColumns(col.code)}
        >
          <div className='pe-1'>
            {isSimpleNumericColumnType(col) ? (
              <>
                {Object.values(InputOverrideType).map((type) => (
                  <InputOverrideField
                    key={type}
                    type={type}
                    columnCode={col.code}
                    {...props}
                    unitTypeMap={unitTypeMap[col.code]}
                  />
                ))}
              </>
            ) : (
              <MandatoryForm
                mandatory={mandatory}
                columnCode={col.code}
                onToggleSwitch={props.onToggleSwitch}
              />
            )}
          </div>
        </DashboardSectionCollapsible>
      ))}
    </>
  );
};
